import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isCustomer, setIsCustomer] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      // Check for admin token first
      const adminToken = localStorage.getItem('token');
      const userToken = localStorage.getItem('user_token');
      const staffToken = localStorage.getItem('staff_token');
      
      let authenticated = false;
      
      if (adminToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${adminToken}` }
          });
          setUser(response.data);
          setToken(adminToken);
          setIsAdmin(response.data.is_admin || false);
          setIsCustomer(false);
          authenticated = true;
        } catch (error) {
          // Admin token is invalid, remove it
          localStorage.removeItem('token');
          localStorage.removeItem('user');
          // Continue to check other tokens
        }
      }
      
      // If not authenticated via admin token, try user token
      if (!authenticated && userToken) {
        // Customer token - validate with backend
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${userToken}` }
          });
          setUser(response.data);
          setToken(userToken);
          setIsAdmin(false);
          setIsCustomer(true);
          authenticated = true;
          // Update stored user data with fresh data
          localStorage.setItem('user_data', JSON.stringify(response.data));
        } catch (error) {
          // User token is invalid or expired, remove it
          console.log('User token invalid, clearing...');
          localStorage.removeItem('user_token');
          localStorage.removeItem('user_data');
        }
      }
      
      // If not authenticated via user token, try staff token
      if (!authenticated && staffToken) {
        // Staff token - validate with backend
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${staffToken}` }
          });
          setUser(response.data);
          setToken(staffToken);
          setIsAdmin(false);
          setIsCustomer(false);
          authenticated = true;
          // Update stored staff data with fresh data
          localStorage.setItem('staff_user', JSON.stringify(response.data));
        } catch (error) {
          // Staff token is invalid or expired, remove it
          console.log('Staff token invalid, clearing...');
          localStorage.removeItem('staff_token');
          localStorage.removeItem('staff_user');
        }
      }
      
      setLoading(false);
    };
    initAuth();
  }, []);

  // Admin login (username/password)
  const adminLogin = async (username, password) => {
    const response = await axios.post(`${API_URL}/api/admin/login`, { username, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setIsAdmin(true);
    setIsCustomer(false);
    return userData;
  };

  // Admin register
  const adminRegister = async (username, email, password) => {
    const response = await axios.post(`${API_URL}/api/admin/register`, { username, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('token', newToken);
    setToken(newToken);
    setUser(userData);
    setIsAdmin(true);
    setIsCustomer(false);
    return userData;
  };

  // User login (email/password)
  const userLogin = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/user/login`, { email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('user_token', newToken);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setIsAdmin(false);
    setIsCustomer(true);
    return userData;
  };

  // User register
  const userRegister = async (name, email, password) => {
    const response = await axios.post(`${API_URL}/api/user/register`, { name, email, password });
    const { token: newToken, user: userData } = response.data;
    localStorage.setItem('user_token', newToken);
    localStorage.setItem('user_data', JSON.stringify(userData));
    setToken(newToken);
    setUser(userData);
    setIsAdmin(false);
    setIsCustomer(true);
    return userData;
  };

  // Legacy login/register for backward compatibility
  const login = adminLogin;
  const register = adminRegister;

  const logout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('user_token');
    localStorage.removeItem('user_data');
    localStorage.removeItem('staff_token');
    localStorage.removeItem('staff_user');
    setToken(null);
    setUser(null);
    setIsAdmin(false);
    setIsCustomer(false);
  };

  const getAuthHeader = () => ({
    headers: { Authorization: `Bearer ${token}` }
  });

  return (
    <AuthContext.Provider value={{
      user,
      token,
      loading,
      isAuthenticated: !!token,
      isAdmin,
      isCustomer,
      login,
      register,
      adminLogin,
      adminRegister,
      userLogin,
      userRegister,
      logout,
      getAuthHeader
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
